import React, { useCallback, useMemo, useState } from "react";
import { Props, State } from "./interfaces";
import { Alert, Button, Dialog, DialogContent, Typography, useTheme } from "@mui/material";
import DialogTitle from "../DialogTitle/DialogTitle";
import { useTranslation } from "react-i18next";
import DialogActions from "../DialogActions/DialogActions";
import { DiscardOutgoingMailApiService } from "../../services/outgoing-mail/discard-outgoing-mail-api.service";
import { LoadResponse } from "../../models/api/load";
import { OutgoingMailDTO } from "../../models/outgoing-mail";
import { IncomingMailDTO } from "../../models/incoming-mail";
import { DiscardIncomingMailApiService } from "../../services/incoming-mail/discard-incoming-mail-api.service";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";
import { INCOMING_MAIL_STATUSES, OUTGOING_MAIL_STATUSES } from "src/utils/constants/constants";
const DiscardMail = ({ outgoingMail, incomingMail, handleNotifyParent }: Props) => {
  const theme = useTheme();
  const tRP = "components.discard_mail";
  const { t } = useTranslation();

  const INITIAL_STATE: State = useMemo(
    () => ({
      open: false,
      loading: false,
      CCDOpen: false,
      error: "",
    }),
    []
  );

  const [state, setState] = useState<State>({
    ...INITIAL_STATE,
  });

  const handleDiscard = useCallback(async () => {
    setState((state) => ({ ...state, loading: true }));
    try {
      let response: LoadResponse<OutgoingMailDTO | IncomingMailDTO> | null = null;
      if (incomingMail) {
        response = await new DiscardIncomingMailApiService().loadItems({
          ids: [incomingMail.id],
        });
      } else if (outgoingMail) {
        response = await new DiscardOutgoingMailApiService().loadItems({
          ids: [outgoingMail.id],
        });
      }

      if (response?.error) {
        setState((state) => ({ ...state, error: response?.error ?? "" }));
      } else {
        setState({ ...INITIAL_STATE });
        handleNotifyParent();
      }
    } catch (error) {
      setState((state) => ({ ...state, error: error as string }));
    } finally {
      setState((state) => ({ ...state, loading: false }));
    }
  }, [INITIAL_STATE, handleNotifyParent, incomingMail, outgoingMail]);

  return (
    <>
      {outgoingMail?.status?.id === OUTGOING_MAIL_STATUSES.Discarded.id ||
      incomingMail?.status?.id === INCOMING_MAIL_STATUSES.Discarded.id ? (
        <Typography
          title={`${t(`${tRP}.already_discarded`)}`}
          color={theme.palette.secondary.light}
        >{`${t(`${tRP}.button_title`)}`}</Typography>
      ) : incomingMail?.status?.id === INCOMING_MAIL_STATUSES.Emailed.id ||
        outgoingMail?.status?.id === OUTGOING_MAIL_STATUSES.Posted.id ? (
        <Typography
          title={`${t(`${tRP}.already_sent_not_allowed`)}`}
          color={theme.palette.secondary.light}
        >{`${t(`${tRP}.button_title`)}`}</Typography>
      ) : (
        <Button
          style={{ color: theme.palette.text.primary }}
          onClick={() => setState((state) => ({ ...state, open: true }))}
          title={`${t(`${tRP}.button_title`)}`}
        >{`${t(`${tRP}.button_title`)}`}</Button>
      )}
      <Dialog
        open={state.open}
        fullWidth
      >
        <DialogTitle
          title={`${t(`${tRP}.title`)}`}
          handleClose={() => setState((state) => ({ ...state, open: false }))}
        />
        <DialogContent>
          <LoadingOverlay
            loading={state.loading}
            error={state.error}
          >
            <Alert severity="warning">{`${t(`${tRP}.info`)}`}</Alert>
          </LoadingOverlay>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, open: false }))}
          handleSave={handleDiscard}
          customSaveName={`${t(`${tRP}.yes_discard`)}`}
        />
      </Dialog>
    </>
  );
};

export default DiscardMail;
